// Per-frame render loop + window-resize handler.
//
// startLoop(deps) returns a handle exposing onResize.

import * as THREE from 'three';
import type Stats from 'stats-gl';
import type { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import type { RotatingObject, LoopHandle, SceneCamera } from '../app/context';

interface LoopDeps {
    stats: Stats | null;
    clock: THREE.Clock | null;
    /** Read live each frame — a projection swap recreates camera + controls,
     *  and the loop must pick up the new instances without a restart. */
    getCamera: () => SceneCamera | null;
    getControls: () => OrbitControls | null;
    scene: THREE.Scene;
    renderer: THREE.WebGLRenderer;
    rotatingObjects: RotatingObject[];
    gridLabels: THREE.Object3D[];
    coordLabels: THREE.Object3D[];
    handleKeyboardInput: (dt: number) => void;
    /** Per-frame: whether to run controls.update(). Fly mode returns false —
     *  OrbitControls would otherwise snap the camera back to the orbit spherical
     *  even with enabled=false. Defaults to always-update when omitted. */
    shouldUpdateControls?: () => boolean;
    updateCoordinateCameraDetails: () => void;
    /** Per-frame selection-marker hover/click animation + zoom-aware sizing
     *  (no-op until built). Takes the live camera to scale by view distance/zoom. */
    updateMarker?: (dt: number, camera: SceneCamera) => void;
    /** Per-frame: fade the vertical-drag knob in/out by camera view angle.
     *  Per-frame (not event-driven) because OrbitControls damping keeps the
     *  camera gliding after pointer release. */
    tickHandleOrientation?: () => void;
    /** Per-frame: keep the pinned value/position panel glued to the marker. */
    tickSelectionTooltip?: () => void;
    /**
     * Optional per-frame hook invoked after the main scene render. Used by
     * the orientation gnomon to draw its own tiny canvas; the loop stays
     * ignorant of who is doing what — it just hands over the frame delta.
     */
    onAfterRender?: (dt: number) => void;
}

export function startLoop(deps: LoopDeps): LoopHandle {
    const {
        stats, clock, getCamera, getControls, scene, renderer,
        rotatingObjects, gridLabels, coordLabels,
        handleKeyboardInput, shouldUpdateControls, updateCoordinateCameraDetails,
        updateMarker, tickHandleOrientation, tickSelectionTooltip, onAfterRender,
    } = deps;

    function animate(): void {
        requestAnimationFrame(animate);

        // stats-gl patches renderer.render to bracket begin()/end() itself
        // (and to time the GPU pass), so the loop only drives the per-frame
        // panel refresh + query drain via update().
        const camera = getCamera();
        const controls = getControls();

        const dt = clock ? clock.getDelta() : 0.016;
        handleKeyboardInput(dt);
        if (controls && (!shouldUpdateControls || shouldUpdateControls())) controls.update();
        try {
            if (Array.isArray(rotatingObjects) && rotatingObjects.length) {
                rotatingObjects.forEach((r) => {
                    if (!r || !r.mesh || !r.axis) return;
                    const angleRad = (r.speedDeg || 0) * (Math.PI / 180) * dt;
                    r.mesh.rotateOnAxis(r.axis, angleRad);
                });
            }
        } catch { /* swallow */ }

        if (camera) {
            if (coordLabels) coordLabels.forEach((label) => { label.lookAt(camera.position); });
            if (gridLabels) gridLabels.forEach((label) => { label.lookAt(camera.position); });
        }
        updateCoordinateCameraDetails();
        if (tickHandleOrientation) tickHandleOrientation();
        if (updateMarker && camera) updateMarker(dt, camera);
        if (tickSelectionTooltip) tickSelectionTooltip();

        if (camera) renderer.render(scene, camera);
        if (onAfterRender) onAfterRender(dt);

        if (stats) stats.update();
    }

    function onResize(): void {
        const camera = getCamera();
        if (camera) {
            const aspect = window.innerWidth / window.innerHeight;
            if (camera instanceof THREE.OrthographicCamera) {
                const halfH = (camera.top - camera.bottom) / 2;
                camera.left = -halfH * aspect;
                camera.right = halfH * aspect;
            } else {
                camera.aspect = aspect;
            }
            camera.updateProjectionMatrix();
        }
        renderer.setSize(window.innerWidth, window.innerHeight);
    }

    animate();

    return { onResize };
}
